import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../firebase';

const initialSellerForm = {
  name: '',
  email: '',
  phone: '',
  quota: '',
  uid: '',
};

const AdminSales = () => {
  const [sellers, setSellers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSeller, setSavingSeller] = useState(false);
  const [assigningLeadId, setAssigningLeadId] = useState(null);
  const [selectedSellerFilter, setSelectedSellerFilter] = useState('all');
  const [form, setForm] = useState(initialSellerForm);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [sellersSnap, leadsSnap] = await Promise.all([
          getDocs(query(collection(db, 'salesAgents'), orderBy('name'))),
          getDocs(collection(db, 'leads')),
        ]);

        setSellers(
          sellersSnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
        );
        setLeads(
          leadsSnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
        );
      } catch (err) {
        console.error('❌ Error cargando ventas:', err);
        setMessage('No se pudieron cargar los datos de ventas.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSellerInput = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddSeller = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setMessage('Nombre y correo son obligatorios.');
      return;
    }
    setSavingSeller(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        quota: Number(form.quota) || 0,
        uid: form.uid.trim() || null,
        active: true,
        createdAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, 'salesAgents'), payload);

      let createdUid = payload.uid || null;
      try {
        const res = await fetch('https://us-central1-tarjeta-ad945.cloudfunctions.net/inviteSalesAgent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: payload.email, name: payload.name }),
        });
        const result = await res.json();
        if (res.ok && result.uid) {
          createdUid = result.uid;
          await updateDoc(doc(db, 'salesAgents', ref.id), { uid: result.uid });
        } else if (!res.ok) {
          console.error('❌ Error invitando vendedor:', result.error);
        }
      } catch (inviteError) {
        console.error('❌ Error enviando invitación:', inviteError);
      }

      setSellers((prev) => [...prev, { id: ref.id, ...payload, uid: createdUid }]);
      setForm(initialSellerForm);
      setMessage('Vendedor registrado e invitación enviada.');
    } catch (err) {
      console.error('❌ Error agregando vendedor:', err);
      setMessage('No se pudo registrar el vendedor.');
    } finally {
      setSavingSeller(false);
    }
  };

  const handleAssignLead = async (leadId, sellerId) => {
    if (!sellerId) return;
    const seller = sellers.find((s) => s.id === sellerId);
    if (!seller) return;
    setAssigningLeadId(leadId);
    try {
      const leadRef = doc(db, 'leads', leadId);
      await updateDoc(leadRef, {
        assignedTo: seller.id,
        assignedToEmail: seller.email,
        assignedToName: seller.name,
        assignedToPhone: seller.phone || null,
        assignedToUid: seller.uid || null,
        assignedAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'leads', leadId, 'timeline'), {
        summary: `Asignado a ${seller.name}`,
        channel: 'sistema',
        createdAt: serverTimestamp(),
        createdBy: 'admin',
        nextStep: null,
      });
      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? {
                ...lead,
                assignedTo: seller.id,
                assignedToEmail: seller.email,
                assignedToName: seller.name,
              }
            : lead
        )
      );
      setMessage(`Lead asignado a ${seller.name}`);
    } catch (err) {
      console.error('❌ Error asignando lead:', err);
      setMessage('No se pudo asignar el lead.');
    } finally {
      setAssigningLeadId(null);
    }
  };

  const filteredLeads = useMemo(() => {
    if (selectedSellerFilter === 'all') return leads;
    return leads.filter(
      (lead) =>
        lead.assignedTo === selectedSellerFilter ||
        lead.assignedToEmail ===
          sellers.find((s) => s.id === selectedSellerFilter)?.email
    );
  }, [leads, selectedSellerFilter, sellers]);

  const sellerSummaries = useMemo(() => {
    return sellers.map((seller) => {
      const sellerLeads = leads.filter(
        (lead) => lead.assignedTo === seller.id || lead.assignedToEmail === seller.email
      );
      const won = sellerLeads.filter(
        (lead) => (lead.status || '').toLowerCase() === 'convertido'
      );
      const hot = sellerLeads.filter((lead) => (lead.aiScore || 0) >= 80);
      const active = sellerLeads.length - won.length;
      return {
        seller,
        total: sellerLeads.length,
        won: won.length,
        active,
        hot: hot.length,
      };
    });
  }, [sellers, leads]);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Equipo de ventas</h2>
        <p className="text-gray-500">Administra vendedores, asignaciones y desempeño.</p>
      </div>

      {message && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-2 rounded">
          {message}
        </div>
      )}

      <section className="grid gap-4 md:grid-cols-3">
        {sellerSummaries.map(({ seller, total, hot, active, won }) => (
          <div key={seller.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">{seller.name}</h3>
              <span className="text-xs text-gray-500">{seller.email}</span>
            </div>
            <dl className="text-sm text-gray-600 space-y-1">
              <div className="flex justify-between">
                <dt>Total leads</dt>
                <dd className="font-semibold text-gray-900">{total}</dd>
              </div>
              <div className="flex justify-between">
                <dt>En proceso</dt>
                <dd>{active}</dd>
              </div>
              <div className="flex justify-between">
                <dt>Hot leads</dt>
                <dd>{hot}</dd>
              </div>
              <div className="flex justify-between">
                <dt>Ganados</dt>
                <dd className="text-emerald-600 font-semibold">{won}</dd>
              </div>
            </dl>
          </div>
        ))}
        {sellerSummaries.length === 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-orange-700">
            No hay vendedores registrados aún.
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Registrar vendedor</h3>
        <form className="grid gap-4 md:grid-cols-2" onSubmit={handleAddSeller}>
          <input
            type="text"
            name="name"
            placeholder="Nombre"
            value={form.name}
            onChange={handleSellerInput}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />
          <input
            type="email"
            name="email"
            placeholder="Correo"
            value={form.email}
            onChange={handleSellerInput}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />
          <input
            type="tel"
            name="phone"
            placeholder="Teléfono"
            value={form.phone}
            onChange={handleSellerInput}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />
          <input
            type="text"
            name="uid"
            placeholder="UID (opcional)"
            value={form.uid}
            onChange={handleSellerInput}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />
          <input
            type="number"
            name="quota"
            placeholder="Meta mensual ($)"
            value={form.quota}
            onChange={handleSellerInput}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />
          <div>
            <button
              type="submit"
              disabled={savingSeller}
              className="bg-orange-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-orange-600 disabled:opacity-60"
            >
              {savingSeller ? 'Guardando...' : 'Agregar vendedor'}
            </button>
          </div>
        </form>
      </section>

      <section className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Leads</h3>
            <p className="text-sm text-gray-500">
              Asigna leads directamente a cada vendedor.
            </p>
          </div>
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={selectedSellerFilter}
            onChange={(e) => setSelectedSellerFilter(e.target.value)}
          >
            <option value="all">Todos los leads</option>
            {sellers.map((seller) => (
              <option key={seller.id} value={seller.id}>
                {seller.name}
              </option>
            ))}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-gray-600">Lead</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-600">Estado</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-600">Asignado a</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-600">Acción</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredLeads.map((lead) => (
                <tr key={lead.id}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{lead.name || '—'}</p>
                    <p className="text-gray-500">{lead.email}</p>
                    <p className="text-gray-400 text-xs">{lead.source || 'origen desconocido'}</p>
                  </td>
                  <td className="px-4 py-3 capitalize">
                    {(lead.status || 'nuevo').replace('_', ' ')}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {lead.assignedToName ? (
                      <>
                        <p className="font-medium text-gray-900">{lead.assignedToName}</p>
                        <p className="text-gray-500">{lead.assignedToEmail}</p>
                      </>
                    ) : (
                      <span className="text-gray-400">Sin asignar</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={lead.assignedTo || ''}
                      onChange={(e) => handleAssignLead(lead.id, e.target.value)}
                      disabled={assigningLeadId === lead.id}
                    >
                      <option value="">Seleccionar vendedor</option>
                      {sellers.map((seller) => (
                        <option key={seller.id} value={seller.id}>
                          {seller.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
              {filteredLeads.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    No hay leads disponibles con el filtro seleccionado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {loading && <p className="text-sm text-gray-500">Cargando datos...</p>}
    </div>
  );
};

export default AdminSales;
